import React from "react";
import { Link } from "react-router-dom";
import { Award, ShieldCheck, Users, Sparkles, ArrowRight, CircleCheck, Phone } from "lucide-react";
import { SITE } from "../lib/site";
import { useLocation as useGeo } from "../contexts/LocationContext";
import SectionHeading from "../components/SectionHeading";
import useDocumentTitle from "../hooks/useDocumentTitle";

const ABOUT_IMG = "https://images.unsplash.com/photo-1621905251918-48416bd8575a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBlbGVjdHJpY2lhbiUyMHdvcmtpbmd8ZW58MHx8fHwxNzc4MTAyMDczfDA&ixlib=rb-4.1.0&q=85";

const VALUES = [
  { icon: ShieldCheck, title: "Sérieux", desc: "Travail soigné, respect des engagements et des délais." },
  { icon: Award, title: "Professionnalisme", desc: "Équipe certifiée, formée aux dernières normes en vigueur." },
  { icon: Users, title: "Proximité", desc: "Un interlocuteur unique, à l'écoute et disponible." },
  { icon: Sparkles, title: "Transparence", desc: "Devis clairs, tarifs justes, aucune mauvaise surprise." },
];

export default function APropos() {
  const { city } = useGeo();
  useDocumentTitle(
    city
      ? `À propos — Votre électricien à ${city} | Voltis`
      : "À propos — Voltis Électricité, électricien certifié",
    city
      ? `Plus de 15 ans d'expérience à ${city} et alentours. Équipe certifiée, garantie décennale.`
      : "Plus de 15 ans d'expérience, 2400+ interventions, équipe certifiée et garantie décennale."
  );
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16 py-16 lg:py-24">
          <div className="lg:col-span-6 flex flex-col justify-center">
            <span className="accent-bar mb-6" />
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-slate-900 mb-3">
              À propos
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
              {city ? (
                <>Votre électricien à <span className="text-[#EAB308]">{city}</span> depuis {SITE.yearsExp}+ ans.</>
              ) : (
                <>{SITE.yearsExp}+ ans à votre service.</>
              )}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              {SITE.brand} est née de la volonté simple d'offrir un service d'électricité fiable,
              rapide et juste — sans jargon, sans surprise. {city ? `Particuliers et professionnels à ${city}` : "Particuliers comme professionnels"} nous
              font confiance pour leurs urgences et leurs projets.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <div className="font-heading text-3xl font-bold text-slate-900">{SITE.yearsExp}+</div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">Années</div>
              </div>
              <div>
                <div className="font-heading text-3xl font-bold text-slate-900">{SITE.interventions}+</div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">Interventions</div>
              </div>
              <div>
                <div className="font-heading text-3xl font-bold text-slate-900">98%</div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">Satisfaits</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={ABOUT_IMG} alt="Notre équipe d'électriciens" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-padding bg-slate-50">
        <div className="container-x">
          <SectionHeading
            eyebrow="Nos valeurs"
            title="Quatre principes simples."
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white border border-slate-200 p-7 hover:border-slate-900 transition-colors">
                <v.icon className="w-9 h-9 text-[#EAB308] mb-5" strokeWidth={1.5} />
                <h4 className="font-heading text-lg font-bold text-slate-900 mb-2">{v.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="section-padding bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Certifications"
              title="Travaux conformes & assurés."
              description="Nous travaillons exclusivement selon les normes en vigueur, avec une assurance décennale et une garantie complète sur nos prestations."
            />
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-4">
              {[
                "Habilitation électrique B1V / BR",
                "Conformité norme NF C 15-100",
                "Assurance responsabilité civile professionnelle",
                "Garantie décennale",
                "Devis gratuit et facturation transparente",
                "Matériel certifié CE",
              ].map((c) => (
                <li key={c} className="flex items-start gap-3 border-b border-slate-100 pb-4">
                  <CircleCheck className="w-5 h-5 text-[#EAB308] mt-0.5 shrink-0" />
                  <span className="text-slate-700">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F172A] text-white">
        <div className="container-x py-16 lg:py-20 flex flex-col lg:flex-row gap-8 items-center justify-between">
          <div>
            <span className="accent-bar mb-5" />
            <h2 className="font-heading text-3xl lg:text-4xl font-bold leading-tight">
              On en parle ?
            </h2>
            <p className="mt-3 text-slate-300 text-lg">
              Devis gratuit sous 24h, conseils sans engagement.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href={`tel:${SITE.phoneTel}`} className="btn-primary justify-center">
              <Phone className="w-5 h-5" /> {SITE.phoneDisplay}
            </a>
            <Link to="/contact" className="bg-white text-slate-900 hover:bg-slate-100 inline-flex items-center justify-center gap-2 px-6 py-3.5 font-semibold rounded-sm transition-colors">
              Nous contacter <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
